import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Chip, IconButton, Input, Option, Select, Tooltip, Typography } from '@mui/joy';
import { MagnifyingGlass, ArrowsClockwise, LinkSimple, Columns } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColorPaletteProp } from '@mui/joy';
import { KanbanBoardView } from '../../../components/kanban';
import type { KanbanColumnModel, KanbanMoveArgs } from '../../../components/kanban';
import { AdminBoardsAPI, AdminDevAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { DevTicket, DevTicketPriority, DevTicketType } from '../../../types/admin';
import { DevTicketDrawer } from './DevTicketDrawer';
import { tagColor } from './ticketUtils';
import { CreateDevTicketModal } from './CreateDevTicketModal';
import { BoardColumnsModal } from '../../../components/admin/BoardColumnsModal';

const TICKET_PAGE_SIZE = 200;

const TYPE_META: Record<DevTicketType, { label: string; color: ColorPaletteProp }> = {
  bug: { label: 'Bug', color: 'danger' },
  feature: { label: 'Feature', color: 'primary' },
  improvement: { label: 'Improvement', color: 'success' },
  chore: { label: 'Chore', color: 'neutral' },
};

// Only the two that mean "look at this sooner" get a chip. Showing every
// priority makes the urgent ones stop standing out.
const PRIORITY_META: Partial<Record<DevTicketPriority, { label: string; color: ColorPaletteProp }>> = {
  high: { label: 'High', color: 'warning' },
  urgent: { label: 'Urgent', color: 'danger' },
};

/**
 * The development board.
 *
 * Same Kanban as the CRM pipeline — one board implementation, two card types.
 * This file supplies only what is specific to a dev ticket.
 */
export const AdminDevelopmentPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const assignee = Number(searchParams.get('assigned_to')) || undefined;

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [archived, setArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [tag, setTag] = useState('');
  const tagsQuery = useQuery({ queryKey: ['admin', 'devTags'], queryFn: () => AdminDevAPI.listTags() });
  const [moveError, setMoveError] = useState<string | null>(null);
  // /manage/development/42 opens DEV-42 directly, which is where the bell's
  // dev-update notifications point.
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeTicketId = Number(params['*']) || null;

  // The open drawer is derived from the URL, with the local choice remembered
  // against the route it was made on. A new route id therefore wins without an
  // effect that re-syncs state on every render pass.
  const [chosen, setChosen] = useState<{ route: number | null; id: number | null }>({
    route: routeTicketId,
    id: routeTicketId,
  });
  const openTicketId = chosen.route === routeTicketId ? chosen.id : routeTicketId;
  const setOpenTicketId = (id: number | null) => setChosen({ route: routeTicketId, id });
  const [editingColumns, setEditingColumns] = useState(false);
  const [creating, setCreating] = useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const boardQuery = useQuery({
    queryKey: ['admin', 'boards', 'dev'],
    queryFn: () => AdminBoardsAPI.list('dev'),
  });

  const board = boardQuery.data?.results.find((b) => b.is_default) ?? boardQuery.data?.results[0];

  const ticketsQuery = useQuery({
    queryKey: ['admin', 'devTickets', { search, board: board?.id, archived, page, assignee, tag }],
    queryFn: () =>
      AdminDevAPI.listTickets({
        search: search || undefined,
        tag: tag || undefined,
        board: board?.id,
        assigned_to: assignee,
        archived,
        page,
        page_size: TICKET_PAGE_SIZE,
      }),
    enabled: board !== undefined,
    placeholderData: (previousData, previousQuery) =>
      (previousQuery?.queryKey[2] as { archived?: boolean } | undefined)?.archived === archived
        ? previousData
        : undefined,
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, boardStatus, position }: { id: number; boardStatus: number; position: number }) =>
      AdminDevAPI.moveTicket(id, boardStatus, position),
    // The server reindexes the column and may notify linked support tickets,
    // so its answer is the only reliable picture of the result.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['admin', 'devTickets'] }),
  });

  const columns: KanbanColumnModel[] = useMemo(
    () =>
      (board?.statuses ?? []).map((status) => ({
        id: status.id,
        name: status.name,
        order: status.order,
        color: status.color,
        is_done: status.is_done,
        wip_limit: status.wip_limit,
      })),
    [board]
  );

  const handleMove = async ({ card, toColumnId, toIndex }: KanbanMoveArgs<DevTicket>) => {
    setMoveError(null);
    await moveMutation.mutateAsync({ id: card.id, boardStatus: toColumnId, position: toIndex });
  };

  const renderCard = (ticket: DevTicket) => {
    const type = TYPE_META[ticket.ticket_type];
    const priority = PRIORITY_META[ticket.priority];
    const linked = ticket.linked_support_tickets.length;

    return (
      <Box sx={{ display: 'grid', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontFamily: 'code' }}>
            {ticket.reference}
          </Typography>
          {ticket.release_version && (
            <Typography level="body-xs" sx={{ color: 'success.600' }}>
              {ticket.release_version}
            </Typography>
          )}
          {ticket.archived_at && <Chip size="sm" variant="soft" color="neutral">Archived</Chip>}
        </Box>
        <Typography level="title-sm">{ticket.title}</Typography>
        {!!ticket.tags?.length && <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{ticket.tags.map(value => <Chip key={value} size="sm" variant="soft" color={tagColor(value)}>{value}</Chip>)}</Box>}
        {(ticket.due_date || ticket.checklist?.length || ticket.attachments?.length) ? <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{[
          ticket.due_date ? `Due ${new Date(`${ticket.due_date}T12:00:00`).toLocaleDateString()}` : '',
          ticket.checklist?.length ? `${ticket.checklist.filter(item => item.is_done).length}/${ticket.checklist.length} checked` : '',
          ticket.attachments?.length ? `${ticket.attachments.length} file${ticket.attachments.length === 1 ? '' : 's'}` : '',
        ].filter(Boolean).join(' · ')}</Typography> : null}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25, alignItems: 'center' }}>
          <Chip size="sm" variant="soft" color={type.color}>
            {type.label}
          </Chip>
          {priority && (
            <Chip size="sm" variant="soft" color={priority.color}>
              {priority.label}
            </Chip>
          )}
          {linked > 0 && (
            <Tooltip
              size="sm"
              variant="soft"
              title={`${linked} customer ticket${linked === 1 ? '' : 's'} will be told when this ships`}
            >
              <Chip size="sm" variant="outlined" color="neutral" startDecorator={<LinkSimple size={12} />}>
                {linked}
              </Chip>
            </Tooltip>
          )}
        </Box>
        {ticket.assigned_to_name && (
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
            {ticket.assigned_to_name}
          </Typography>
        )}
      </Box>
    );
  };

  const toolbar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {assignee && <Button size="sm" variant="soft" onClick={() => { const next = new URLSearchParams(searchParams); next.delete('assigned_to'); setSearchParams(next); setPage(1); }}>Assigned staff filter · Clear</Button>}
      <CollapsibleFilters><Input
        size="sm"
        placeholder="Search tickets"
        value={searchInput}
        onChange={(event) => {
          setSearchInput(event.target.value);
          setPage(1);
        }}
        startDecorator={<MagnifyingGlass size={16} />}
        sx={{ minWidth: { xs: 0, sm: 240 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
      /></CollapsibleFilters>
      <Select
        size="sm"
        aria-label="Ticket status"
        value={archived ? 'archived' : 'active'}
        onChange={(_, value) => {
          if (!value) return;
          setArchived(value === 'archived');
          setPage(1);
        }}
      >
        <Option value="active">Active tickets</Option>
        <Option value="archived">Archived tickets</Option>
      </Select>
      <Select size="sm" aria-label="Filter tickets by tag" value={tag} onChange={(_, value) => { if (value != null) { setTag(value); setPage(1); } }}>
        <Option value="">All tags</Option>
        {tagsQuery.data?.map(value => <Option key={value} value={value}>{value}</Option>)}
      </Select>
      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
        {ticketsQuery.data?.count ?? 0} tickets
      </Typography>
      {moveError && (
        <Typography level="body-xs" color="danger">
          {moveError}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      {(page > 1 || ticketsQuery.data?.next) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="sm" variant="plain" disabled={page === 1 || ticketsQuery.isFetching} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <Typography level="body-xs">Page {page}</Typography>
          <Button size="sm" variant="plain" disabled={!ticketsQuery.data?.next || ticketsQuery.isFetching} onClick={() => setPage(p => p + 1)}>Next</Button>
        </Box>
      )}
      <Button size="sm" variant="solid" onClick={() => {
        // Creation and viewing are mutually exclusive, including after a deep link.
        setChosen({ route: null, id: null });
        if (routeTicketId) navigate({ pathname: '/manage/development', search: location.search });
        setCreating(true);
      }}>
        New ticket
      </Button>
      <IconButton
        size="sm"
        variant="outlined"
        color="neutral"
        aria-label="Edit columns"
        onClick={() => setEditingColumns(true)}
      >
        <Columns size={16} />
      </IconButton>
      <IconButton
        size="sm"
        variant="outlined"
        color="neutral"
        aria-label="Refresh"
        onClick={() => queryClient.invalidateQueries({ queryKey: ['admin', 'devTickets'] })}
      >
        <ArrowsClockwise size={16} />
      </IconButton>
    </Box>
  );

  return (
    <>
      <DevTicketDrawer
        key={openTicketId}
        ticketId={creating ? null : openTicketId}
        onClose={() => {
          setChosen({ route: null, id: null });
          if (routeTicketId) navigate({ pathname: '/manage/development', search: location.search });
        }}
      />
      <BoardColumnsModal
        open={editingColumns}
        onClose={() => setEditingColumns(false)}
        boardId={board?.id ?? null}
        kind="dev"
      />
      {creating && (
        <CreateDevTicketModal
          boardStatus={board?.statuses?.[0]?.id ?? null}
          onClose={() => setCreating(false)}
          onCreated={(ticket) => {
            setCreating(false);
            setArchived(false);
            setPage(1);
            queryClient.invalidateQueries({ queryKey: ['admin', 'devTickets'] });
            setOpenTicketId(ticket.id);
          }}
        />
      )}
      <KanbanBoardView<DevTicket>
      dragDisabled={archived || ticketsQuery.isPlaceholderData}
      boardScope="admin-dev"
      columns={columns}
      cards={ticketsQuery.data?.results ?? []}
      getCardId={(ticket) => ticket.id}
      getColumnId={(ticket) => ticket.board_status}
      getPosition={(ticket) => ticket.position}
      renderCard={renderCard}
      onCardClick={(ticket) => setOpenTicketId(ticket.id)}
      onMove={handleMove}
      onMoveError={(error) =>
        setMoveError(getApiErrorMessage(error, 'That move could not be saved.'))
      }
      boardColor={board?.color}
      isLoading={boardQuery.isLoading || ticketsQuery.isLoading}
      isFetching={ticketsQuery.isFetching || moveMutation.isPending}
      error={(boardQuery.error ?? ticketsQuery.error) as Error | null}
      onRetry={() => {
        boardQuery.refetch();
        ticketsQuery.refetch();
      }}
      toolbar={toolbar}
      emptyLabel="Nothing in this column"
      emptyColumnsSlot={
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          The Development board has no columns yet.
        </Typography>
      }
      />
    </>
  );
};

export default AdminDevelopmentPanel;
